import { Box, Group, Loader, Portal, Stack, Text } from '@mantine/core';
import StepRunsRenderer from '@/pages/Run/components/StepExecutionRenderer.tsx';
import useLatestStepRuns from '@/hooks/api/stepExecutions/useLatestStepRuns.ts';
import useWaitingStepDependencies from '@/hooks/api/stepExecutions/useWaitingStepDependencies.ts';
import CustomSpinner from '@/components/Spinner.tsx';
import RunInlineEmptyState from '@/pages/Run/components/RunInlineEmptyState.tsx';
import {
  AutomationStep,
  ConditionalStep,
  FailureHandlerStep,
} from '@/types/automationSteps.ts';
import { useCallback, useMemo } from 'react';
import { Run } from '@/types/runs.ts';
import {
  getStepExecutionChildScopeState,
  ParentStepRun,
  RetainedPriorScopeStepRun,
} from '@/types/stepExecutions.ts';
import { SortingFunction } from '@/hooks/api/sse/runEvents/utils.ts';
import useRetrieveRunAutomationVersion from '@/hooks/api/runs/useRetrieveRunAutomationVersion.ts';
import { ConditionAction, StepType } from '@/types/automationFields.ts';
import { ExecutionStatus } from '@/types/statuses.ts';
import {
  buildStepDisabledInfoById,
  isStepWithDirectChildren,
} from '@/utils/stepUtils.ts';
import { AutoExpandPlan } from '@/types/autoExpandPlan.ts';
import { ChildScopeContext } from '@/pages/Run/components/ScopeStack/childScopeModel.ts';
import { buildRunPresentationSteps } from '@/utils/runStepPresentation.ts';

type props = {
  run: Run;
  enabled: boolean;
  parentSteps: ParentStepRun[];
  parentPath?: string;
  childIndex?: number;
  attemptNumber?: number;
  scopeParentStepRunId?: string;
  stepsById: Record<string, AutomationStep>;
  autoExpandPlan?: AutoExpandPlan | null;
  childScopePresentation?: 'inline' | 'horizontal';
  onOpenChildScope?: (context: ChildScopeContext, isHistorical?: boolean) => void;
  onChildScopeContextChange?: (context: ChildScopeContext) => void;
  activeScopeParentStepRunId?: string;
};

const StepExecutionsRenderer = ({
  run,
  enabled,
  parentSteps,
  parentPath,
  childIndex,
  attemptNumber,
  scopeParentStepRunId,
  stepsById,
  autoExpandPlan,
  childScopePresentation = 'inline',
  onOpenChildScope,
  onChildScopeContextChange,
  activeScopeParentStepRunId,
}: props) => {
  const { data: stepRunsByStepId, isPending: loadingStepRuns } = useLatestStepRuns(
    {
      parentPath,
      childIndex,
      attemptNumber,
      scopeParentStepRunId:
        childIndex !== undefined ? scopeParentStepRunId ?? parentSteps.at(-1)?.id : undefined,
    },
    enabled,
  );
  // Waiting dependencies reflect the current live scope state. Historical attempt views use
  // attempt-aware step runs instead of mixing in current-state blocking diagnostics.
  const shouldLoadWaitingStepDependencies = enabled && attemptNumber === undefined;
  const waitingDependenciesScope = {
    parentPath,
    childIndex,
    scopeParentStepRunId:
      childIndex !== undefined ? scopeParentStepRunId ?? parentSteps.at(-1)?.id : undefined,
  };
  const { data: waitingStepDependencies } = useWaitingStepDependencies(
    waitingDependenciesScope,
    shouldLoadWaitingStepDependencies,
  );

  const getStepRunsForStepId = useCallback(
    (stepId: string) => {
      if (!stepRunsByStepId || !(stepId in stepRunsByStepId.step_runs_by_step_id)) return [];
      return [...stepRunsByStepId.step_runs_by_step_id[stepId]].sort(SortingFunction);
    },
    [stepRunsByStepId],
  );

  const getRetainedPriorScopeForStepId = useCallback(
    (stepId: string): RetainedPriorScopeStepRun | undefined =>
      stepRunsByStepId?.retained_prior_scope_by_step_id?.[stepId]?.[0],
    [stepRunsByStepId],
  );

  const { data: automationVersion, isPending: loadingAutomationVersion } =
    useRetrieveRunAutomationVersion(run.id);

  const presentationSteps = useMemo(
    () => (automationVersion ? buildRunPresentationSteps(automationVersion) : []),
    [automationVersion],
  );

  const disabledInfoById = useMemo(
    () => buildStepDisabledInfoById(presentationSteps),
    [presentationSteps],
  );

  const isPreparingRerun =
    parentSteps.length === 0 && run.status === ExecutionStatus.RerunPreparing;

  const currentSteps = useMemo(() => {
    if (parentSteps.length === 0) return presentationSteps;
    const parentStepRun = parentSteps.at(-1)!;
    const parentStepId = parentStepRun.step_id;
    const parentStep = parentStepId in stepsById ? stepsById[parentStepId] : null;
    if (!parentStep) return [];
    if (isStepWithDirectChildren(parentStep)) {
      return parentStep.children;
    }
    const childScopeState = getStepExecutionChildScopeState(parentStepRun.step_execution);
    const conditionId =
      childIndex !== undefined ? childScopeState?.[childIndex - 1]?.key : childScopeState?.at(0)?.key;
    if (parentStepRun.step_type === StepType.FailureHandler) {
      const failureHandler = parentStep as FailureHandlerStep;
      return conditionId === 'handler' ? failureHandler.handler_children : failureHandler.children;
    }
    const condition = (parentStep as ConditionalStep).conditions.find(
      (c) => c.condition_id === conditionId,
    );
    return condition?.action === ConditionAction.ChildSteps ? condition.children : [];
  }, [childIndex, parentSteps, presentationSteps, stepsById]);

  if (!enabled) {
    return null;
  }

  if (
    loadingStepRuns ||
    loadingAutomationVersion ||
    stepRunsByStepId === undefined
  ) {
    return <CustomSpinner size={parentSteps.length === 0 ? 'xl' : 'sm'} />;
  }

  if (currentSteps.length === 0) {
    return <RunInlineEmptyState message="No child results" />;
  }

  return (
    <Box pos="relative" w="100%" mih={parentSteps.length === 0 ? 160 : undefined}>
      <Stack
        w={'100%'}
        justify={'center'}
        align={'stretch'}
        maw={'1000px'}
        miw={0}
        gap={childScopePresentation === 'horizontal' ? 8 : 'md'}
      >
        {currentSteps.map((step, index) => (
          <StepRunsRenderer
            run={run}
            step={step}
            stepRuns={getStepRunsForStepId(step.step_id)}
            retainedPriorScope={getRetainedPriorScopeForStepId(step.step_id)}
            key={step.step_id}
            index={index}
            isLastStep={index === currentSteps.length - 1}
            parentSteps={parentSteps}
            allowHistoricalStepRuns={attemptNumber !== undefined}
            stepsById={stepsById}
            disabledInfo={disabledInfoById.get(step.step_id)}
            waitingDependencies={
              waitingStepDependencies?.waiting_dependencies_by_step_id[step.step_id] ?? []
            }
            autoExpandPlan={autoExpandPlan}
            childScopePresentation={childScopePresentation}
            onOpenChildScope={onOpenChildScope}
            onChildScopeContextChange={onChildScopeContextChange}
            activeScopeParentStepRunId={activeScopeParentStepRunId}
          />
        ))}
      </Stack>
      {isPreparingRerun && (
        <Portal>
          <Box
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.72)',
              backdropFilter: 'blur(2px)',
              pointerEvents: 'auto',
            }}
          >
            <Group
              justify="center"
              gap="xs"
              px="md"
              py="sm"
              w="max-content"
              maw="calc(100% - 32px)"
              style={{
                border: '1px solid #DBDBDB',
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.08)',
              }}
            >
              <Loader size="xs" />
              <Text size="sm" fw={500}>
                Preparing rerun...
              </Text>
            </Group>
          </Box>
        </Portal>
      )}
    </Box>
  );
};

export default StepExecutionsRenderer;
